// admin/AdminNavbar.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiMenu, FiUser } from "react-icons/fi";

const NavbarStyles = () => (
  <style>{`
    .admin-navbar {
      height: 70px;
      background-color: #ffffff;
      border-bottom: 1px solid #f1f5f9;
      padding: 0 32px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: sticky;
      top: 0;
      z-index: 998;
      backdrop-filter: blur(10px);
      background: rgba(255, 255, 255, 0.95);
    }

    .navbar-left-section {
      display: flex;
      align-items: center;
      gap: 24px;
    }

    .mobile-menu-button {
      display: none;
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      color: #64748b;
      padding: 8px;
      border-radius: 10px;
      transition: all 0.2s ease;
    }

    .mobile-menu-button:hover {
      background-color: #f8fafc;
      color: #334155;
    }

    .search-container {
      display: flex;
      align-items: center;
      background-color: #f8fafc;
      border-radius: 12px;
      padding: 8px 16px;
      width: 280px;
      border: 1.5px solid #e2e8f0;
      height: 44px;
      transition: all 0.2s ease;
    }

    .search-container:focus-within {
      border-color: #3b82f6;
      background-color: #ffffff;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .search-container input {
      border: none;
      outline: none;
      background-color: transparent;
      width: 100%;
      font-size: 14px;
      color: #334155;
      margin-left: 12px;
      font-weight: 400;
    }

    .search-container input::placeholder {
      color: #94a3b8;
      font-weight: 400;
    }

    .navbar-right-section {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .user-badge {
      display: flex;
      align-items: center;
      gap: 12px;
      background-color: #ffffff;
      padding: 8px 16px 8px 12px;
      border-radius: 12px;
      border: 1.5px solid #f1f5f9;
      height: 44px;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
    }
    
    .user-badge::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    .user-badge:hover {
        background-color: #f8fafc;
        border-color: #e2e8f0;
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    }

    .user-badge:hover::before {
      opacity: 0.02;
    }

    .user-icon {
      width: 32px;
      height: 32px;
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 14px;
      font-weight: 600;
      flex-shrink: 0;
      position: relative;
      z-index: 1;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }

    .user-name {
      font-size: 14px;
      color: #334155;
      font-weight: 500;
      position: relative;
      z-index: 1;
    }

    .profile-icon {
      font-size: 16px;
      color: #64748b;
      position: relative;
      z-index: 1;
      transition: all 0.3s ease;
    }

    .user-badge:hover .profile-icon {
      color: #3b82f6;
      transform: translateX(2px);
    }
    
    /* --- Mobile Styles --- */
    @media (max-width: 767px) {
      .admin-navbar {
        padding: 0 20px;
        height: 64px;
      }

      .mobile-menu-button {
        display: flex;
      }

      .search-container {
        display: none;
      }
      
      .user-name {
        display: none;
      }
      
      .user-badge {
        padding: 8px;
        gap: 0;
        border-radius: 10px;
        height: 40px;
        width: 40px;
        justify-content: center;
      }

      .navbar-right-section {
        gap: 12px;
      }

      .user-icon {
        width: 28px;
        height: 28px;
        font-size: 12px;
        border-radius: 8px;
      }

      .profile-icon {
        display: none;
      }
    }

    @media (max-width: 480px) {
      .admin-navbar {
        padding: 0 16px;
      }
      
      .navbar-right-section {
        gap: 8px;
      }
      
      .user-badge {
        width: 36px;
        height: 36px;
      }

      .user-icon {
        width: 24px;
        height: 24px;
        font-size: 11px;
      }
    }

    /* Animation for smooth interactions */
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .admin-navbar {
      animation: fadeIn 0.5s ease-out;
    }
  `}</style>
);

const AdminNavbar = ({ onMenuToggle }) => {
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  // Load user data from localStorage
  useEffect(() => {
    const storedUserData = localStorage.getItem("userData");
    if (storedUserData) {
      try {
        setUserData(JSON.parse(storedUserData));
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleUserBadgeClick = () => {
    navigate("/admin/profile");
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userData?.full_name) return "A";
    return userData.full_name
      .split(" ")
      .map(name => name[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Get display name
  const getDisplayName = () => {
    if (!userData?.full_name) return "Admin User";
    return userData.full_name.split(" ")[0]; // First name only
  };

  return (
    <>
      <NavbarStyles />
      <div className="admin-navbar">
        {/* Left Section */}
        <div className="navbar-left-section">
          <button className="mobile-menu-button" onClick={onMenuToggle}>
            <FiMenu />
          </button>
          
          <div className="search-container">
            <FiSearch style={{ color: "#64748b", fontSize: "18px" }} />
            <input type="text" placeholder="Search anything..." />
          </div>
        </div>

        {/* Right Section */}
        <div className="navbar-right-section">
          {/* User Profile Badge - Direct link to profile */}
          <div 
            className="user-badge"
            onClick={handleUserBadgeClick}
            title="View Profile"
          >
            <div className="user-icon">{getUserInitials()}</div>
            <span className="user-name">{getDisplayName()}</span>
            <FiUser className="profile-icon" />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminNavbar;